import { type CropRect } from '@/types';
import { type WorkerPhase, type WorkerRequest, type WorkerResponse } from '@/workers/protocol';

export class ConversionCancelledError extends Error {
  constructor() {
    super('Conversion cancelled');
    this.name = 'ConversionCancelledError';
  }
}

export interface ConvertOptions {
  file: File;
  inputName: string;
  paletteArgs: string[];
  encodeArgs: string[];
  outputDuration: number;
  onProgress?: (phase: WorkerPhase, ratio: number) => void;
  onLog?: (message: string) => void;
}

export interface DetectCropOptions {
  file: File;
  inputName: string;
  args: string[];
  sourceWidth: number;
  sourceHeight: number;
}

interface PendingRequest {
  resolve: (value: never) => void;
  reject: (error: Error) => void;
  onProgress?: (phase: WorkerPhase, ratio: number) => void;
  onLog?: (message: string) => void;
}

/**
 * Owns the dedicated FFmpeg worker. The engine is lazy — no worker exists
 * (and no wasm is fetched) until the first request needs it — and `cancel`
 * is a hard terminate, which both stops mid-encode work instantly and
 * releases the entire wasm heap. The next request simply spawns fresh;
 * core assets come back from the Cache API so respawns are cheap.
 */
class FfmpegClient {
  #worker: Worker | null = null;
  #sequence = 0;
  #pending = new Map<number, PendingRequest>();

  #ensureWorker(): Worker {
    if (this.#worker) return this.#worker;
    const worker = new Worker(new URL('../workers/ffmpeg.worker.ts', import.meta.url), {
      type: 'module',
      name: 'loopsmith-ffmpeg',
    });
    worker.onmessage = (event: MessageEvent<WorkerResponse>) => this.#handleMessage(event.data);
    worker.onerror = (event) => {
      this.#failAll(new Error(event.message || 'The conversion engine crashed.'));
      worker.terminate();
      if (this.#worker === worker) this.#worker = null;
    };
    this.#worker = worker;
    return worker;
  }

  #handleMessage(message: WorkerResponse) {
    const pending = this.#pending.get(message.id);
    if (!pending) return;
    switch (message.kind) {
      case 'progress':
        pending.onProgress?.(message.phase, message.ratio);
        break;
      case 'log':
        pending.onLog?.(message.message);
        break;
      case 'ready':
        this.#pending.delete(message.id);
        (pending.resolve as (v: void) => void)();
        break;
      case 'done': {
        this.#pending.delete(message.id);
        const blob = new Blob([message.buffer], { type: 'image/gif' });
        (pending.resolve as (v: Blob) => void)(blob);
        break;
      }
      case 'cropdetect-result':
        this.#pending.delete(message.id);
        (pending.resolve as (v: CropRect | null) => void)(message.crop);
        break;
      case 'error':
        this.#pending.delete(message.id);
        pending.reject(new Error(message.message));
        break;
    }
  }

  #request<T>(
    build: (id: number) => WorkerRequest,
    hooks?: Pick<PendingRequest, 'onProgress' | 'onLog'>,
  ): Promise<T> {
    const worker = this.#ensureWorker();
    const id = ++this.#sequence;
    return new Promise<T>((resolve, reject) => {
      this.#pending.set(id, {
        resolve: resolve as PendingRequest['resolve'],
        reject,
        ...hooks,
      });
      worker.postMessage(build(id));
    });
  }

  /** Optional pre-warm: loads the wasm core without running anything. */
  warmUp(): Promise<void> {
    return this.#request<void>((id) => ({ id, kind: 'load' }));
  }

  convert(options: ConvertOptions): Promise<Blob> {
    const { onProgress, onLog, ...payload } = options;
    return this.#request<Blob>((id) => ({ id, kind: 'convert', ...payload }), {
      onProgress,
      onLog,
    });
  }

  detectCrop(options: DetectCropOptions): Promise<CropRect | null> {
    return this.#request<CropRect | null>((id) => ({ id, kind: 'cropdetect', ...options }));
  }

  /** Hard-stop everything in flight and free the wasm heap. */
  cancelActive(): void {
    if (this.#worker) {
      this.#worker.terminate();
      this.#worker = null;
    }
    this.#failAll(new ConversionCancelledError());
  }

  dispose(): void {
    this.cancelActive();
  }

  #failAll(error: Error) {
    const pending = [...this.#pending.values()];
    this.#pending.clear();
    for (const request of pending) request.reject(error);
  }
}

export const ffmpegClient = new FfmpegClient();
